import type { CmsEditField } from '@eneco/cms-adapter';
import { stegaClean } from '@sanity/client/stega';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** `image-<id>-<width>x<height>-<format>`, the shape of an image asset reference. */
const IMAGE_REF = /^image-([A-Za-z0-9]+)-(\d+x\d+)-([A-Za-z0-9]+)$/;

/**
 * Fields whose value is a design-system lookup key rendered as an illustration.
 *
 * The same names `stega-filter.ts` refuses to encode, and for the same reason from the other side:
 * an encoded name cannot be looked up, and a name that cannot be found in the rendered output
 * cannot be marked. `icon` names an illustration too (`MegaMenuV2`).
 */
export const ILLUSTRATION_FIELDS: ReadonlySet<string> = new Set(['icon', 'illustration']);

/** A block far deeper than this is a shape we do not understand; stop rather than recurse on it. */
const MAX_DEPTH = 8;

/**
 * The image and illustration fields of one raw block, with the document path of each.
 *
 * **From the raw block, not from `content`.** The migration folds same-shaped fields into shared
 * `role`-keyed arrays (ADR-0068 decision 8) and `unsweepFamilies` unfolds them again for components,
 * so `content.ribbonContent` lives at `richText[_key=="…"].content` in the document. A path built
 * from what a component reads would be a path the Studio cannot open; one built from the block is
 * correct by construction, because it is the document's own structure.
 *
 * `isSlotArray` is the adapter's own test for "this array holds child blocks". Those are separate
 * renderings with their own fields, so they are skipped here rather than reported twice. **Only a
 * block's direct fields are tested**, exactly as the adapter does: deeper down, an array of typed
 * objects is content, not children. `MegaMenuV2`'s `navItem`/`navGroup` entries all carry a `_type`
 * and would each pass the test, so applying it at every depth silently dropped their illustrations.
 *
 * A projected document — an object with its own `_id` and `_type`, as `siteWideContent->{ … }`
 * yields — is a boundary. What lies inside it is not part of the page document: a path such as
 * `siteWideContent.items[…]` names a reference there, not the items. Its fields are reported
 * relative to that document, with its identity attached, so the Studio opens the right one.
 *
 * An array entry without a `_key` cannot be addressed and is skipped, for the same reason
 * `itemPath` in the adapter falls back to the array. Only ever called in draft mode.
 */
export const collectEditFields = (
  block: Record<string, unknown>,
  basePath: string,
  isSlotArray: (value: unknown) => boolean,
): CmsEditField[] => {
  const found: CmsEditField[] = [];

  const visit = (
    value: unknown,
    path: string,
    field: string | undefined,
    depth: number,
    document: CmsEditField['document'],
  ): void => {
    if (depth > MAX_DEPTH) return;
    const at = document ? { document } : {};

    if (Array.isArray(value)) {
      // Direct fields only. See the note on `isSlotArray` above.
      if (depth === 0 && isSlotArray(value)) return;
      for (const item of value) {
        if (!isObject(item) || typeof item._key !== 'string' || item._key === '') continue;
        visit(item, `${path}[_key=="${item._key}"]`, undefined, depth + 1, document);
      }
      return;
    }

    if (typeof value === 'string') {
      const name = stegaClean(value);
      if (field !== undefined && ILLUSTRATION_FIELDS.has(field) && name !== '') {
        found.push({ path, kind: 'illustration', match: name, ...at });
      }
      return;
    }

    if (!isObject(value)) return;

    const ref = isObject(value.asset) ? value.asset._ref : undefined;
    const image = typeof ref === 'string' ? IMAGE_REF.exec(ref) : null;
    if (image) {
      // The CDN file name — `<id>-<w>x<h>.<ext>` — is what the rendered `src` ends in.
      found.push({ path, kind: 'image', match: `${image[1]}-${image[2]}.${image[3]}`, ...at });
      return;
    }

    // A document inlined by a projection: paths inside it start over, and say whose they are.
    const isDocument = typeof value._id === 'string' && typeof value._type === 'string';
    const scope = isDocument ? { id: value._id as string, type: value._type as string } : document;
    const base = isDocument ? '' : path;

    for (const [key, child] of Object.entries(value)) {
      if (key.startsWith('_')) continue;
      visit(child, base ? `${base}.${key}` : key, key, depth + 1, scope);
    }
  };

  for (const [key, value] of Object.entries(block)) {
    if (key.startsWith('_') || key === 'area' || key === 'parentKey') continue;
    visit(value, basePath ? `${basePath}.${key}` : key, key, 0, undefined);
  }

  return found;
};
